<template>
  <aside class="sidebar" :class="{ 'sidebar--open': isOpen }">
    <!-- LOGO -->
    <div
      class="p-6 flex items-center justify-between gap-3 border-b dark:border-white/5"
    >
      <div class="flex items-center gap-1">
        <div
          class="w-9 h-9 rounded-xl overflow-hidden flex items-center justify-center"
        >
          >
          <img
            src="/favicon.ico"
            alt="Logo"
            class="w-full h-full object-contain"
          />
        </div>
        <span
          class="text-xl font-black uppercase italic text-slate-800 dark:text-white"
        >
          idey<span class="text-primary">Admin</span>
        </span>
      </div>
      <!-- Close button – mobile/tablet only -->
      <button class="close-btn lg:hidden" @click.stop="close">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- NAV -->
    <nav class="flex-1 mt-6 overflow-y-auto px-4 pb-4 custom-scrollbar">
      <ul class="space-y-2">
        <!-- DASHBOARD -->
        <li>
          <router-link
            to="/dashboard"
            class="nav-link"
            active-class="active-link"
          >
            <i class="fas fa-chart-line icon"></i>
            {{ $t("dashboard") }}
          </router-link>
        </li>

        <li v-if="canViewAnalytics">
          <router-link to="/analytics" class="nav-link" active-class="active-link">
            <i class="fas fa-chart-pie icon"></i>
            Analytics
          </router-link>
        </li>

        <!-- USERS -->
        <template v-if="isAdmin || isModerator">
          <li class="section-title">{{ $t("users") }}</li>
          <li>
            <router-link
              to="/users"
              class="nav-link"
              active-class="active-link"
            >
              <i class="fas fa-users icon"></i>
              {{ $t("users_list") }}
            </router-link>
          </li>
          <li v-if="isAdmin">
            <router-link
              to="/moderators"
              class="nav-link"
              active-class="active-link"
            >
              <i class="fas fa-user-shield icon"></i>
              {{ $t("moderators") }}
            </router-link>
          </li>
          <li>
            <router-link
              to="/leads"
              class="nav-link"
              active-class="active-link"
            >
              <i class="fas fa-user-tag icon"></i>
              {{ $t("leads") }}
            </router-link>
          </li>
          <li v-if="isAdmin">
            <router-link
              to="/activityLogs"
              class="nav-link"
              active-class="active-link"
            >
              <i class="fas fa-stream icon"></i>
              {{ $t("activity_logs") }}
            </router-link>
          </li>
          <li v-if="isAdmin">
            <router-link
              to="/owners"
              class="nav-link"
              active-class="active-link"
            >
              <i class="fas fa-id-badge icon"></i>
              {{ $t("owners_management") }}
            </router-link>
          </li>
          <li v-if="isAdmin">
            <router-link
              to="/roles"
              class="nav-link"
              active-class="active-link"
            >
              <i class="fas fa-shield-halved icon"></i>
              {{ $t("roles") }}
            </router-link>
          </li>
          <li v-if="isAdmin">
            <router-link to="/otps" class="nav-link" active-class="active-link">
              <i class="fas fa-key icon"></i>
              {{ $t("otp") }}
            </router-link>
          </li>
          <li v-if="isAdmin">
            <router-link
              to="/admin/chats"
              class="nav-link"
              active-class="active-link"
            >
              <i class="fas fa-comments icon"></i>
              {{ $t("messages") }}
            </router-link>
          </li>
          <li v-if="isAdmin">
            <router-link
              to="/admin/notifications"
              class="nav-link"
              active-class="active-link"
            >
              <i class="fas fa-bell icon"></i>
              {{ $t("push_notifications") }}
            </router-link>
          </li>
          <li v-if="isAdmin || isModerator">
            <router-link
              to="/admin/whatsapp"
              class="nav-link"
              active-class="active-link"
            >
              <i class="fab fa-whatsapp icon"></i>
              {{ $t("whatsapp_inbox") }}
            </router-link>
          </li>
        </template>

        <!-- FLEET -->
        <li class="section-title">{{ $t("fleet") }}</li>
        <li>
          <router-link to="/cars" class="nav-link" active-class="active-link">
            <i class="fas fa-car icon"></i>
            {{ $t("cars") }}
          </router-link>
        </li>

        <!-- BOOKINGS -->
        <li v-if="isAdmin || isModerator">
          <router-link
            to="/admin/bookings"
            class="nav-link"
            active-class="active-link"
          >
            <i class="fas fa-calendar-check icon"></i>
            {{ $t("bookings") }}
          </router-link>
        </li>

        <li v-if="isHost && !isAdmin && !isModerator">
          <router-link
            to="/host/bookings"
            class="nav-link"
            active-class="active-link"
          >
            <i class="fas fa-file-invoice icon"></i>
            {{ $t("my_bookings") }}
          </router-link>
        </li>

        <!-- MASTER DATA -->
        <template v-if="isAdmin || isModerator">
          <li class="section-title">{{ $t("master_data") }}</li>
          <li>
            <router-link
              to="/brand"
              class="nav-link"
              active-class="active-link"
            >
              <i class="fas fa-tags icon"></i>
              {{ $t("brand") }}
            </router-link>
          </li>
          <li>
            <router-link
              to="/categories"
              class="nav-link"
              active-class="active-link"
            >
              <i class="fas fa-folder-tree icon"></i>
              {{ $t("category") }}
            </router-link>
          </li>
          <li>
            <router-link
              to="/promo-codes"
              class="nav-link"
              active-class="active-link"
            >
              <i class="fas fa-ticket-alt icon"></i>
              {{ $t("promo_codes") }}
            </router-link>
          </li>
          <li>
            <router-link
              to="/tutorial-videos"
              class="nav-link"
              active-class="active-link"
            >
              <i class="fas fa-video icon"></i>
              {{ $t("tutorial_videos") }}
            </router-link>
          </li>

          <!-- SYSTEM DATA -->
          <li class="section-title">{{ $t("system_data") }}</li>
          <li>
            <button
              @click="openSystemData = !openSystemData"
              class="nav-link w-full flex justify-between items-center"
            >
              <span class="flex items-center gap-3">
                <i class="fas fa-database icon"></i>
                {{ $t("system_data") }}
              </span>
              <i
                class="fas fa-chevron-down transition-transform duration-300"
                :class="{ 'rotate-180': openSystemData }"
              ></i>
            </button>
          </li>
          <transition name="fade">
            <div v-show="openSystemData" class="pl-4 space-y-2 mt-2">
              <li v-for="item in masterDataLinks" :key="item.to">
                <router-link
                  :to="item.to"
                  class="nav-link text-xs"
                  active-class="active-link"
                >
                  <i :class="item.icon + ' icon'"></i>
                  {{ $t(item.label) }}
                </router-link>
              </li>
            </div>
          </transition>
        </template>
      </ul>
    </nav>

    <!-- LOGOUT -->
    <div class="p-4 border-t dark:border-white/5">
      <button
        @click="logout"
        class="nav-link w-full justify-center hover:bg-red-500 hover:text-white"
      >
        <i class="fas fa-right-from-bracket icon"></i>
        {{ $t("logout") }}
      </button>
    </div>
  </aside>
</template>

<script>
import authService from "@/services/auth.service";

export default {
  name: "AppSidebar",

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:isOpen"],

  data() {
    return {
      openSystemData: true,
    };
  },

  watch: {
    $route() {
      this.close();
    },
  },

  computed: {
    user() {
      return authService.getUser();
    },
    roles() {
      return this.user?.roles || [];
    },
    isAdmin() {
      return this.roles.includes("admin");
    },
    isModerator() {
      return this.roles.includes("moderator");
    },
    canAccessPanel() {
      return this.roles.includes("admin");
    },
    canViewAnalytics() {
      return this.isAdmin || this.roles.includes("individual_owner") || this.roles.includes("organization_owner");
    },
    masterDataLinks() {
      return [
        {
          to: "/master-data/vehicle-types",
          label: "vehicle_types",
          icon: "fas fa-truck-pickup",
        },
        {
          to: "/master-data/fuel-types",
          label: "fuel_types",
          icon: "fas fa-gas-pump",
        },
        {
          to: "/master-data/transmission-types",
          label: "transmission_types",
          icon: "fas fa-cog",
        },
        { to: "/master-data/features", label: "features", icon: "fas fa-star" },
        {
          to: "/master-data/pickup-options",
          label: "pickup_options",
          icon: "fas fa-map-location-dot",
        },
        {
          to: "/master-data/pickup-option-locations",
          label: "pickup_option_locations",
          icon: "fas fa-map-pin",
        },
        {
          to: "/master-data/elevate-experiences",
          label: "elevate_experiences",
          icon: "fas fa-gem",
        },
        {
          to: "/master-data/car-rules",
          label: "car_rules",
          icon: "fas fa-list-check",
        },
        {
          to: "/master-data/protection-plans",
          label: "protection_plans",
          icon: "fas fa-file-shield",
        },
        {
          to: "/master-data/extras",
          label: "extras",
          icon: "fas fa-plus-circle",
        },
      ];
    },
  },

  methods: {
    close() {
      this.$emit("update:isOpen", false);
    },
    logout() {
      authService.logout();
      this.$router.push("/login");
    },
  },
};
</script>

<style scoped>
/* ── Sidebar shell ─────────────────────────────────── */
.sidebar {
  position: fixed;
  inset: 0 auto 0 0; /* full height, anchored left */
  z-index: 50;
  width: 256px;
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #fff;
  color: #64748b;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border-right: 1px solid #e2e8f0;
  transform: translateX(-100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

.sidebar.sidebar--open {
  transform: translateX(0);
}

/* On large screens sidebar is always visible and part of flow */
@media (min-width: 1024px) {
  .sidebar {
    position: static;
    transform: none !important;
    box-shadow: none;
    border-right: 1px solid #e2e8f0;
  }
}

/* Dark mode */
:global(.dark) .sidebar {
  background-color: #0b0d14;
  border-right-color: rgba(255, 255, 255, 0.05);
}

/* ── Close button ──────────────────────────────────── */
.close-btn {
  padding: 6px 8px;
  border-radius: 8px;
  color: #94a3b8;
  transition: all 0.2s;
  font-size: 1rem;
}
.close-btn:hover {
  color: #0f172a;
  background-color: #f1f5f9;
}
:global(.dark) .close-btn:hover {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.1);
}

/* ── Nav links ─────────────────────────────────────── */
.nav-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 14px;
  color: #64748b;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid transparent;
  width: 100%;
}
.nav-link:hover {
  background-color: #f1f5f9;
  color: #0f172a;
  border-color: rgba(0, 0, 0, 0.05);
  transform: translateY(-1px);
}
:global(.dark) .nav-link:hover {
  background-color: #1e2330;
  color: #f8fafc;
  border-color: rgba(255, 255, 255, 0.05);
}
.active-link {
  background-color: #3d5cff !important;
  color: white !important;
  box-shadow: 0 8px 20px rgba(61, 92, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.1);
}

.section-title {
  padding: 1.5rem 1rem 0.5rem 1rem;
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: #94a3b8;
  list-style: none;
}
:global(.dark) .section-title {
  color: #334155;
}

.icon {
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  flex-shrink: 0;
}

/* ── Scrollbar ─────────────────────────────────────── */
.custom-scrollbar::-webkit-scrollbar {
  width: 3px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #e2e8f0;
  border-radius: 10px;
}
:global(.dark) .custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #1e2330;
}

/* ── Dropdown transition ───────────────────────────── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
